import CancelIcon from '@material-ui/icons/Cancel'
import { unwrapResult } from '@reduxjs/toolkit'
import { useActor, useInterpret } from '@xstate/react'
import queryString from 'query-string'
import React, { createContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { generatePath, useHistory, useLocation, useParams } from 'react-router'
import { Event } from 'xstate'

import {
  Applicant,
  ApplicationBookingState,
  PublicApplicationData,
} from '@flatsy/types/Applications'
import { FetchStatus } from '@flatsy/types/FetchStatus'
import {
  toFrontendApplicationBookingState,
  toFrontendApplicationState,
} from '@flatsy/utils/Conditions/Application'
import { createGlobalErrors } from '@flatsy/utils/Functions/form/Errors'
import * as ROUTES from 'src/constants/Routes'
import { useApplicantById } from 'src/hooks/ApplicantsSelector'
import { useApplicationDetails } from 'src/hooks/ApplicationDetails'
import { useAssignedAgentId } from 'src/hooks/AssignedAgent'
import { useBookableSlots } from 'src/hooks/BookableSlots'
import { AppDispatch } from 'src/redux'
import { AlertSlice } from 'src/redux/slices/Alert'
import { saveApplicant as saveApplicantReduxAction } from 'src/redux/slices/Applicant'
import {
  applicationSlice,
  completeApplication as completeApplicationReduxAction,
  saveApplication as saveApplicationReduxAction,
} from 'src/redux/slices/Application'
import { takeBooking } from 'src/redux/slices/Application/Action'
import { getApplicationAgentIdOr } from 'src/utils/AgentId'
import { includeBenefits } from 'src/utils/IncludeBenefits'
import { isPinel } from 'src/utils/IsPinel'

import { isInWaitingList } from './Conditions/WaitingList'
import { stepsName } from './Constant'
import { ContextProps, ReturnContextProps, WizardScreen } from './Type'
import { getActionAndApplicantFromStep } from './Utils/CurrentSteps'
import { sanitizeApplicant } from './Utils/SanitizeApplicant'
import { sanitizeApplicationObject } from './Utils/SanitizeApplication'
import { WizardEvent, WizardNextEventParams, wizardMachine } from './wizardMachine'

export type { ContextProps, ReturnContextProps }
export const FormContext = createContext<ReturnContextProps | null>(null)

export const FormProvider: React.FC<ContextProps> = ({ children }) => {
  /* -------------------------------------------------------------------------- */
  /*                            REACT, REDUX, ROUTER                            */
  /* -------------------------------------------------------------------------- */
  const dispatch: AppDispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const search = queryString.parse(location.search)
  const { agent } = search
  /**
   * @ApplicationId : Id of application
   * @Id : Asset reference when we don't have applicationId ex : agence_1_locAsset3555
   */
  const { applicationId, id } = useParams<{
    applicationId?: string
    id?: string
  }>()

  /* -------------------------------------------------------------------------- */
  /*                            WIZARD STATE MACHINE                            */
  /* -------------------------------------------------------------------------- */
  const wizardService = useInterpret(wizardMachine)
  const [currentWizard] = useActor(wizardService)
  const { send } = wizardService
  const currentApplicant = useApplicantById(currentWizard.context.applicantId)

  /* -------------------------------------------------------------------------- */
  /*                             APPLICATION DETAILS                            */
  /* -------------------------------------------------------------------------- */
  const {
    applicants,
    application,
    applicantsStatus,
    applicationStatus,
    asset,
    qualification,
    assetRequestStatus,
    qualificationStatus,
    fetch,
  } = useApplicationDetails()

  /* -------------------------------------------------------------------------- */
  /*                             ASSIGNED AGENT ID                              */
  /* -------------------------------------------------------------------------- */
  const assignedAgentId = useAssignedAgentId(application, agent)

  /* -------------------------------------------------------------------------- */
  /*                               BOOKABLE SLOTS                               */
  /* -------------------------------------------------------------------------- */
  const { slots, slotsStatus, getBookables } = useBookableSlots({
    assetId: asset?.id,
    agent: assignedAgentId,
  })

  /* -------------------------------------------------------------------------- */
  /*                               INTERNAL STATE                               */
  /* -------------------------------------------------------------------------- */
  const [loading, setLoading] = useState(false)
  const [inWaitingList, setInWaitingList] = useState(false)

  /* -------------------------------------------------------------------------- */
  /*                                    UTILS                                   */
  /* -------------------------------------------------------------------------- */

  const formatStep = (): string => {
    const array = currentWizard.toStrings()
    if (array) {
      return `${array[array.length - 1]}${
        currentWizard.context.applicantId ? `/${currentWizard.context.applicantId}` : ''
      }`
    }
    return ''
  }

  const getCurrentStepTitle = (): string => {
    const array = currentWizard.toStrings()
    if (array) {
      return stepsName(currentApplicant, array[1])
    }
    return ''
  }

  const getComputed = (application: false | PublicApplicationData) => {
    if (application) {
      return {
        ...application,
        computedState: toFrontendApplicationState(application.state, application.booking_state),
        computedBookingState: toFrontendApplicationBookingState(application),
      }
    }
    return application
  }

  /* -------------------------------------------------------------------------- */
  /*                           DISPATCH ERROR IN REDUX                          */
  /* -------------------------------------------------------------------------- */
  const dispatchFormError = (error: unknown) => {
    if (error) {
      const errorTypes = error as { validation_error: any; errors: any }
      dispatch(
        AlertSlice.actions.setAlert({
          severity: 'error',
          content: createGlobalErrors(errorTypes.validation_error, errorTypes.errors),
          open: true,
          icon: <CancelIcon data-testid="close" fontSize="inherit" />,
        })
      )
      setLoading(false)
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             NEXT WITHOUT EVENT                             */
  /* -------------------------------------------------------------------------- */
  /**
   * @description save application information in redux and local storage and go to next
   */
  const nextStep = () => {
    send('NEXT', {
      value: {},
    })
  }

  /* ------------------------------------------------------------------------------ */
  /*                             PREVIOUS WITHOUT EVENT                             */
  /* ------------------------------------------------------------------------------ */
  /**
   * @description save application information in redux and local storage and go to previous
   */
  const previousStep = async () => {
    if (applicationId) {
      setLoading(true)
      const fetchResult = await fetch(currentWizard.context?.application?.id)
      setLoading(false)
      send('BACK', {
        value: {
          application: fetchResult.application,
          applicants: fetchResult.applicants,
        },
      })
    } else {
      send('BACK', {
        value: {},
      })
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                      SAVE APPLICATION IN LOCAL STORAGE                     */
  /* -------------------------------------------------------------------------- */
  /**
   * @description save application information in redux and local storage and go to next
   */
  const saveApplicationInRedux = (application: PublicApplicationData) => {
    dispatch(applicationSlice.actions.saveLocalInformation(application))
    send('NEXT', {
      value: {
        application: getComputed(application),
      },
    })
  }

  /* -------------------------------------------------------------------------- */
  /*             REDIRECT TO APPLICATION URL WHEN APPLICATION EXIST             */
  /* -------------------------------------------------------------------------- */
  const redirectToApplicationPath = (applicationId?: string) => {
    if (applicationId) {
      history.push({
        pathname: `${generatePath(ROUTES.APPLICATION, {
          applicationId,
        })}`,
      })
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                    SAVE APPLICATION, GET, AND GO TO NEXT                   */
  /* -------------------------------------------------------------------------- */
  const saveApplicationAndContinue = async (application: PublicApplicationData) => {
    try {
      if (asset && asset.id) {
        setLoading(true)
        const baseApplication = !currentWizard.context.application?.id
          ? currentWizard.context.application // POST
          : { id: currentWizard.context.application?.id } // PATCH (only needs the id)
        const sanitizedApplication = sanitizeApplicationObject(application)

        const saveApplicationAction = await dispatch(
          saveApplicationReduxAction({
            ...baseApplication,
            ...sanitizedApplication,
            step: formatStep(),
            agent_id: getApplicationAgentIdOr(agent, application),
            asset: asset.url_id,
          })
        )
        const result = unwrapResult(saveApplicationAction)
        const fetchResult = await fetch(result.id)
        redirectToApplicationPath(result.id)
        setLoading(false)
        send('NEXT', {
          value: {
            application: getComputed(fetchResult.application),
            applicants: fetchResult.applicants,
          },
        })
      }
    } catch (err) {
      setLoading(false)
      dispatchFormError(err)
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                     SAVE APPLICANT, GET AND GO TO NEXT                     */
  /* -------------------------------------------------------------------------- */
  const saveApplicantAndContinue = async (ctx: Applicant, newApplicant: Partial<Applicant>) => {
    const applicant = {
      applicant_id: ctx.applicant_id,
      id: ctx.id,
      kind: ctx.kind,
      ...newApplicant,
    }

    try {
      if (application.application.id) {
        setLoading(true)
        const sanitizedApplicant = sanitizeApplicant(applicant)
        dispatch(
          saveApplicantReduxAction({
            ...sanitizedApplicant,
            id: applicant.id,
            applicant_id: application.application.id,
          })
        )
          .then((saveApplicantAction) => {
            dispatch(
              saveApplicationReduxAction({
                step: formatStep(),
                id: application.application.id,
              })
            )
              .then((saveApplicationAction) => {
                const applicationResult = unwrapResult(saveApplicationAction)
                unwrapResult(saveApplicantAction)
                fetch(applicationResult.id)
                  .then((fetchResult) => {
                    setLoading(false)

                    send('NEXT', {
                      value: {
                        application: getComputed(fetchResult.application),
                        applicants: fetchResult.applicants,
                      },
                    })
                  })
                  .catch((err) => {
                    setLoading(false)
                    dispatchFormError(err)
                  })
              })
              .catch((err) => {
                setLoading(false)
                dispatchFormError(err)
              })
          })
          .catch((err) => {
            setLoading(false)
            dispatchFormError(err)
          })
      } else {
        setLoading(false)
        dispatchFormError({
          validation_error: undefined,
          errors: ['error.global.bad_request'],
        })
      }
    } catch (err) {
      dispatchFormError(err)
    }
  }

  const canTakeBooking = (
    result: PublicApplicationData | undefined
  ):
    | {
        start_date: string
        start_time: string
        applicationId: string
      }
    | false => {
    if (
      result &&
      result.id &&
      result.booking_wanted_date &&
      result.booking_wanted_time &&
      result.booking_state === ApplicationBookingState.AUTHORIZED
    ) {
      return {
        start_date: result.booking_wanted_date,
        start_time: result.booking_wanted_time,
        applicationId: result.id,
      }
    }
    return false
  }

  const completeApplication = async () => {
    if (currentWizard.context.application) {
      setLoading(true)
      dispatch(completeApplicationReduxAction(currentWizard.context.application.id || ''))
        .then(async (res) => {
          const result = unwrapResult(res)
          const data = canTakeBooking(result)
          if (data) {
            dispatch(
              takeBooking({
                start_date: data.start_date,
                start_time: data.start_time,
                applicationId: data.applicationId,
              })
            )
              .then((res) => {
                unwrapResult(res)
                fetch(result.id).then((fetchResult) => {
                  setLoading(false)
                  send('NEXT', {
                    value: {
                      application: getComputed(fetchResult.application),
                      applicants: fetchResult.applicants,
                    },
                  })
                })
              })
              .catch(() => {
                setLoading(false)
                fetch(applicationId)
                  .then(async (fetchResult) => {
                    setLoading(false)
                    const bookables = await getBookables()
                    send('NEXT', {
                      value: {
                        application: getComputed(fetchResult.application),
                        applicants: fetchResult.applicants,
                        bookingError: true,
                        slots: bookables,
                      },
                    })
                  })
                  .catch((err) => {
                    setLoading(false)
                    dispatchFormError(err)
                  })
              })
            // catch booking error
          } else {
            setLoading(false)
            const fetchResult = await fetch(result.id)
            let value: WizardNextEventParams = {
              asset: undefined,
              agentId: undefined,
              application: getComputed(fetchResult.application) || undefined,
              applicants: fetchResult.applicants || undefined,
              qualification: undefined,
              slots: undefined,
              bookingError: false,
            }
            if (currentWizard.context.asset?.status !== 'standby') {
              // We are not in the mode with the user not booking its visit at
              // first so it is a real booking error
              const bookables = await getBookables()
              value = { ...value, bookingError: true, slots: bookables }
            }
            send('NEXT', { value })
          }
        })
        .catch(() => {
          setLoading(false)
          fetch(applicationId)
            .then((fetchResult) => {
              setLoading(false)
              send('NEXT', {
                value: {
                  application: getComputed(fetchResult.application),
                  applicants: fetchResult.applicants,
                },
              })
            })
            .catch((err) => {
              setLoading(false)
              dispatchFormError(err)
            })
        })
    }
  }

  useEffect(() => {
    if (currentWizard.value === WizardScreen.end) {
      completeApplication()
    }
  }, [currentWizard.value])

  /* -------------------------------------------------------------------------- */
  /*                          WAIT FOR GETTING ALL DATA                         */
  /* -------------------------------------------------------------------------- */
  /**
   * Application not created
   */
  const StartQualificationNoApplication =
    asset &&
    qualification &&
    slots &&
    assetRequestStatus === FetchStatus.SUCCEEDED &&
    qualificationStatus === FetchStatus.SUCCEEDED &&
    slotsStatus === FetchStatus.SUCCEEDED &&
    currentWizard.value === 'idle'

  const StartQualificationWithApplication =
    applicationId &&
    application &&
    applicants &&
    applicantsStatus === FetchStatus.SUCCEEDED &&
    applicationStatus === FetchStatus.SUCCEEDED

  useEffect(() => {
    if (!applicationId && StartQualificationNoApplication) {
      send('NEXT', {
        value: {
          asset,
          agentId: assignedAgentId,
          qualification,
          slots,
        },
      })
    }
  }, [
    asset,
    qualification,
    applicationId,
    assetRequestStatus,
    qualificationStatus,
    slots,
    slotsStatus,
    currentWizard.value,
  ])

  /**
   * Application created
   */
  useEffect(() => {
    if (StartQualificationNoApplication && StartQualificationWithApplication) {
      const converted = getActionAndApplicantFromStep(application.application.step || '')
      let formatAction: Event<WizardEvent> = 'NEXT'
      if (converted.action) {
        formatAction = converted.action as Event<WizardEvent>
      }
      send(formatAction || 'NEXT', {
        value: {
          asset,
          agentId: assignedAgentId,
          qualification,
          slots,
          application: getComputed(application.application),
          applicants,
          redirect: !!converted.action,
          applicantId: converted.applicant || undefined,
        },
      })
      if (formatAction !== 'NEXT') {
        /**
         * @information : send NEXT to immediately go to the next step
         */
        send('NEXT', {
          value: {
            asset,
            agentId: assignedAgentId,
            qualification,
            slots,
            application: getComputed(application.application),
            applicants,
          },
        })
      }
    }
  }, [
    asset,
    qualification,
    applicants,
    applicantsStatus,
    application,
    applicationStatus,
    applicationId,
    assetRequestStatus,
    qualificationStatus,
    slots,
    slotsStatus,
    currentWizard.value,
  ])

  useEffect(() => {
    if (isInWaitingList(currentWizard.context)) {
      setInWaitingList(true)
    }
  }, [currentWizard.context])

  const benefits = includeBenefits(currentWizard.context.qualification)
  const pinel = isPinel(currentWizard.context.qualification)

  /* -------------------------------------------------------------------------- */
  /*                                   CONTEXT                                  */
  /* -------------------------------------------------------------------------- */
  const contextValues: ReturnContextProps = {
    wizardService,
    wizardContext: currentWizard,
    saveApplicationInRedux,
    saveApplicationAndContinue,
    saveApplicantAndContinue,
    getCurrentStepTitle,
    nextStep,
    previousStep,
    includeBenefits: benefits,
    isPinel: pinel,
    loading,
    inWaitingList,
    paramsUrl: {
      applicationId,
      id,
    },
  }
  return <FormContext.Provider value={contextValues}>{children}</FormContext.Provider>
}

export default FormProvider
